import React, { useState, useEffect, useCallback, useRef } from 'react';
import { C, card, sectionTitle, filterPill, kpiCard, kpiIcon, toggleGroup, toggleBtn } from '../../styles';
import {
  getSalesOverview, getSalesTrend, getSalesDrill,
  getSalesFilterOptions, getSalesAvgValueTrend,
  getVisualPrefs, setVisualPref
} from '../../services/api';
const ReactECharts = require('echarts-for-react').default;

// ─── helpers ─────────────────────────────────────────────────────────────────

const parseLocalDate = (str) => {
  if (!str) return null;
  const s = String(str);
  const iso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3]);
  const d = new Date(s);
  if (!isNaN(d.getTime())) return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return null;
};

const fmtDate = (str, opts) => {
  const d = parseLocalDate(str);
  return d ? d.toLocaleDateString('en-GB', opts) : str;
};

const toISOKey = (str) => {
  if (!str) return '';
  const m = String(str).match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(str);
  if (!isNaN(d.getTime())) {
    const pad = n => String(n).padStart(2,'0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  }
  return String(str);
};

const fillDailyGaps = (data, dateFrom, dateTo) => {
  if (!data.length && !dateFrom) return data;
  const map = {};
  data.forEach(r => { map[toISOKey(r.period)] = r; });
  const start = parseLocalDate(dateFrom || data[0]?.period);
  const end   = parseLocalDate(dateTo   || data[data.length - 1]?.period);
  if (!start || !end) return data;
  const pad = n => String(n).padStart(2, '0');
  const localKey = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const result = [];
  const cur = new Date(start);
  while (cur <= end) {
    const key = localKey(cur);
    result.push(map[key] || { period: key, value: 0 });
    cur.setDate(cur.getDate() + 1);
  }
  return result;
};

const isMultiYear = (data) => {
  if (!data.length) return false;
  const years = new Set(data.map(r => String(r.period).slice(0, 4)));
  return years.size > 1;
};

const generateFilledPeriodKeys = (sortedISO, granularity) => {
  if (!sortedISO.length) return sortedISO;
  const start = parseLocalDate(sortedISO[0]);
  const end   = parseLocalDate(sortedISO[sortedISO.length - 1]);
  if (!start || !end) return sortedISO;
  const pad = n => String(n).padStart(2, '0');
  const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const result = [];
  const cur = new Date(start);
  while (cur <= end) {
    result.push(fmt(cur));
    if (granularity === 'monthly') cur.setMonth(cur.getMonth() + 1);
    else if (granularity === 'weekly') cur.setDate(cur.getDate() + 7);
    else cur.setDate(cur.getDate() + 1);
  }
  return result;
};

// ─── constants ────────────────────────────────────────────────────────────────

const DRILL_COLORS = [C.accent, C.blue, C.purple, C.amber, '#FF6B6B', '#34D399', '#60A5FA', '#F472B6'];

// ─── interactive chart legend helpers (shared) ────────────────────────────────
const useChartLegend = (items) => {
  const [selected, setSelected] = React.useState(() => new Set(items));
  const key = items.join('||');
  React.useEffect(() => { setSelected(new Set(items)); }, [key]); // eslint-disable-line
  const toggle = (ref, name) => {
    ref?.current?.getEchartsInstance?.()?.dispatchAction({ type: 'legendToggleSelect', name });
    setSelected(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  };
  const selectAll = (ref) => {
    ref?.current?.getEchartsInstance?.()?.dispatchAction({ type: 'legendAllSelect' });
    setSelected(new Set(items));
  };
  const resetAll = (ref) => {
    const c = ref?.current?.getEchartsInstance?.();
    if (c) { c.dispatchAction({ type: 'legendAllSelect' }); c.dispatchAction({ type: 'legendInverseSelect' }); }
    setSelected(new Set());
  };
  return { selected, toggle, selectAll, resetAll };
};

const ChartLegend = ({ items, colors, chartRef, selected, onToggle, onSelectAll, onReset }) => (
  <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:'4px 10px', padding:'6px 0 0' }}>
    <button onClick={() => onSelectAll(chartRef)}
      style={{ fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.accent}`, borderRadius:'4px', color:C.accent, cursor:'pointer', flexShrink:0 }}>
      All
    </button>
    <button onClick={() => onReset(chartRef)}
      style={{ fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.textMuted, cursor:'pointer', flexShrink:0 }}>
      Reset
    </button>
    {items.map((name, i) => {
      const isSel = selected.has(name);
      return (
        <div key={name} onClick={() => onToggle(chartRef, name)}
          style={{ display:'flex', alignItems:'center', gap:'3px', cursor:'pointer', opacity: isSel ? 1 : 0.3, flexShrink:0 }}>
          <div style={{ width:'7px', height:'7px', borderRadius:'50%', background: colors[i % colors.length] }}/>
          <span style={{ fontSize:'9px', color: C.textSec }}>{name}</span>
        </div>
      );
    })}
  </div>
);

const DRILL_DIMS = [
  { key: 'payment_mode',  label: 'Payment Mode',  short: 'Pay Mode'  },
  { key: 'classify',      label: 'Classify',      short: 'Classify'     },
  { key: 'age_group',     label: 'Age Group',     short: 'Age'       },
  { key: 'product_name',  label: 'Product Name',  short: 'Product Name'  },
  { key: 'sold_code',     label: 'Staff',         short: 'Staff'     },
  { key: 'daily',         label: 'Daily Trend',   short: 'Daily'     },
];

const QUICK = [
  { id: 'yesterday',       label: 'Yesterday'        },
  { id: 'this_year',       label: 'This Year'         },
  { id: 'this_month',      label: 'This Month'        },
  { id: 'this_week',       label: 'This Week'         },
  { id: 'last_year',       label: 'Last Year'         },
  { id: 'last_month',      label: 'Last Month'        },
  { id: 'last_week',       label: 'Last Week'         },
  { id: 'last_year_month', label: "Last Year's Month" },
  { id: 'custom',          label: 'Custom'            },
  { id: 'all',             label: 'All Time'          },
];

// ─── sub-components ───────────────────────────────────────────────────────────

const KPICard = ({ title, value, icon, iconBg, sub }) => (
  <div style={kpiCard}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'5px' }}>
      <div style={kpiIcon(iconBg)}>{icon}</div>
    </div>
    <p style={{ fontSize:'11px', color:C.textSec, margin:'0 0 4px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'0.5px' }}>{title}</p>
    <p style={{ fontSize:'16px', fontWeight:'700', color:C.textPrimary, margin:0, lineHeight:1.2 }}>{value}</p>
    {sub && <p style={{ fontSize:'10px', color:C.textMuted, margin:'3px 0 0' }}>{sub}</p>}
  </div>
);

const CollapsibleSection = ({ visKey, title, collapsed, onToggle, headerRight, children }) => (
  <div style={{ ...card, marginBottom: '12px' }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: collapsed ? 0 : '8px' }}>
      <p style={sectionTitle}>{title}</p>
      <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
        {!collapsed && headerRight}
        <button
          onClick={() => onToggle(visKey)}
          style={{ fontSize:'10px', color:C.textSec, background:'none', border:`1px solid ${C.border}`, borderRadius:'6px', padding:'3px 8px', cursor:'pointer' }}
        >
          {collapsed ? 'Expand' : 'Collapse'}
        </button>
      </div>
    </div>
    {!collapsed && children}
  </div>
);

const MultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen]       = useState(false);
  const [search, setSearch]   = useState('');
  const [pending, setPending] = useState(selected);
  const ref = useRef(null);

  useEffect(() => { setPending(selected); }, [selected]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (v) => {
    setPending(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  };

  const filtered = (options || []).filter(opt => {
    const lbl = typeof opt === 'object' ? (opt.label || opt.name || '') : (opt || '');
    return String(lbl).toLowerCase().includes(search.toLowerCase());
  });

  const hasChanges = JSON.stringify([...pending].sort()) !== JSON.stringify([...selected].sort());

  return (
    <div ref={ref} style={{ position:'relative', flex:1, minWidth:'140px' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width:'100%', padding:'4px 10px', background:C.bgSurface, border:`1px solid ${pending.length ? C.accent : C.border}`, borderRadius:'8px', fontSize:'11px', color: pending.length ? C.accent : C.textMuted, textAlign:'left', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}
      >
        <span>{label}{pending.length ? ` (${pending.length})` : ''}</span>
        <span style={{ fontSize:'9px' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:200, background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'8px', marginTop:'4px', boxShadow:'0 4px 12px rgba(0,0,0,0.4)' }}>
          <div style={{ padding:'6px 8px', borderBottom:`1px solid ${C.border}` }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              onClick={e => e.stopPropagation()}
              style={{ width:'100%', padding:'5px 8px', background:C.bgElevated, border:`1px solid ${C.border}`, borderRadius:'6px', fontSize:'11px', color:C.textPrimary, outline:'none', boxSizing:'border-box' }}
            />
          </div>
          <div style={{ maxHeight:'150px', overflowY:'auto' }}>
            {filtered.length === 0 && <p style={{ padding:'8px 10px', fontSize:'11px', color:C.textMuted }}>No results</p>}
            {filtered.map((opt, i) => {
              const val   = typeof opt === 'object' ? (opt.key || opt.code || opt) : opt;
              const lbl   = typeof opt === 'object' ? (opt.label || opt.name || String(opt.key || opt.code || '')) : String(opt || '');
              const isSel = pending.includes(val);
              return (
                <div key={i} onClick={() => toggle(val)} style={{ padding:'7px 10px', fontSize:'11px', color: isSel ? C.accent : C.textPrimary, background: isSel ? C.accentDim : 'transparent', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' }}>
                  <span style={{ width:'12px', height:'12px', border:`1px solid ${isSel ? C.accent : C.border}`, borderRadius:'3px', background: isSel ? C.accent : 'transparent', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {isSel && <span style={{ color:'#000', fontSize:'8px' }}>✓</span>}
                  </span>
                  {lbl}
                </div>
              );
            })}
          </div>
          <div style={{ padding:'6px 8px', borderTop:`1px solid ${C.border}`, display:'flex', gap:'6px', justifyContent:'flex-end' }}>
            {pending.length > 0 && (
              <button onClick={() => { setPending([]); }} style={{ fontSize:'10px', color:C.danger, background:'none', border:'none', cursor:'pointer', padding:'3px 6px' }}>Clear</button>
            )}
            <button
              onClick={() => { onChange(pending); setOpen(false); setSearch(''); }}
              disabled={!hasChanges && pending.length === 0}
              style={{ fontSize:'11px', fontWeight:'600', color: hasChanges || pending.length > 0 ? '#000' : C.textMuted, background: hasChanges || pending.length > 0 ? C.accent : C.bgElevated, border:'none', borderRadius:'6px', padding:'4px 10px', cursor:'pointer' }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const YearSelect = ({ available, selected, onChange }) => {
  const [open, setOpen]       = useState(false);
  const [pending, setPending] = useState(selected);
  const ref = useRef(null);

  useEffect(() => { setPending(selected); }, [selected]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (yr) => setPending(prev => prev.includes(yr) ? prev.filter(x => x !== yr) : [...prev, yr]);
  const hasChanges = JSON.stringify([...pending].sort()) !== JSON.stringify([...selected].sort());

  return (
    <div ref={ref} style={{ position:'relative', minWidth:'130px' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ padding:'4px 10px', background:C.bgSurface, border:`1px solid ${pending.length ? C.accent : C.border}`, borderRadius:'8px', fontSize:'11px', color: pending.length ? C.accent : C.textMuted, cursor:'pointer', display:'flex', gap:'6px', alignItems:'center' }}
      >
        <span>Years{pending.length ? ` (${pending.length})` : ''}</span>
        <span style={{ fontSize:'9px' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'100%', right:0, zIndex:200, background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'8px', marginTop:'4px', boxShadow:'0 4px 12px rgba(0,0,0,0.4)', minWidth:'110px' }}>
          <div style={{ maxHeight:'160px', overflowY:'auto' }}>
            {available.length === 0 && <p style={{ padding:'8px 10px', fontSize:'11px', color:C.textMuted }}>No data yet</p>}
            {available.map((yr, i) => {
              const isSel = pending.includes(yr);
              return (
                <div key={i} onClick={() => toggle(yr)} style={{ padding:'7px 10px', fontSize:'12px', color: isSel ? C.accent : C.textSec, background: isSel ? C.accentDim : 'transparent', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' }}>
                  <span style={{ width:'12px', height:'12px', border:`1px solid ${isSel ? C.accent : C.border}`, borderRadius:'3px', background: isSel ? C.accent : 'transparent', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {isSel && <span style={{ color:'#000', fontSize:'8px' }}>✓</span>}
                  </span>
                  {yr}
                </div>
              );
            })}
          </div>
          <div style={{ padding:'6px 8px', borderTop:`1px solid ${C.border}`, display:'flex', gap:'6px', justifyContent:'flex-end' }}>
            {pending.length > 0 && (
              <button onClick={() => setPending([])} style={{ fontSize:'10px', color:C.danger, background:'none', border:'none', cursor:'pointer', padding:'3px 6px' }}>Clear</button>
            )}
            <button
              onClick={() => { onChange(pending); setOpen(false); }}
              disabled={!hasChanges && pending.length === 0}
              style={{ fontSize:'11px', fontWeight:'600', color: hasChanges || pending.length > 0 ? '#000' : C.textMuted, background: hasChanges || pending.length > 0 ? C.accent : C.bgElevated, border:'none', borderRadius:'6px', padding:'4px 10px', cursor:'pointer' }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Breadcrumb = ({ path, onNavigate }) => (
  <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:'4px', padding:'6px 10px', background:C.bgElevated, borderRadius:'8px', marginBottom:'10px' }}>
    {path.map((item, i) => (
      <span key={i} style={{ display:'flex', alignItems:'center', gap:'4px' }}>
        {i > 0 && <span style={{ color:C.textMuted, fontSize:'11px' }}>›</span>}
        <span
          onClick={() => i < path.length - 1 && onNavigate(i)}
          style={{ fontSize:'11px', fontWeight: i === path.length-1 ? '600' : '400', color: i === path.length-1 ? C.textPrimary : C.accent, cursor: i < path.length-1 ? 'pointer' : 'default', textDecoration: i < path.length-1 ? 'underline' : 'none' }}
        >{item}</span>
      </span>
    ))}
  </div>
);

// ── "Drill deeper by" bottom-sheet picker ─────────────────────────────────────
const DrillDimPicker = ({ open, usedDims, onPick, onCancel }) => {
  if (!open) return null;
  const available = DRILL_DIMS.filter(d => !usedDims.includes(d.key));
  if (available.length === 0) return null;
  return (
    <div
      style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,0.5)' }}
      onClick={onCancel}
    >
      <div
        style={{ position:'absolute', bottom:0, left:0, right:0,
          background:C.bgSurface, borderRadius:'16px 16px 0 0',
          border:`1px solid ${C.border}`, padding:'16px 16px 36px',
          boxShadow:'0 -8px 40px rgba(0,0,0,0.6)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* drag handle */}
        <div style={{ width:'36px', height:'4px', background:C.border, borderRadius:'2px', margin:'0 auto 14px' }} />
        <p style={{ fontSize:'11px', fontWeight:'600', color:C.textMuted, textTransform:'uppercase',
          letterSpacing:'0.8px', marginBottom:'12px', textAlign:'center' }}>
          Drill deeper by
        </p>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'12px' }}>
          {available.map(d => (
            <button key={d.key} onClick={() => onPick(d.key)}
              style={{ padding:'14px 8px', background:C.bgElevated,
                border:`1px solid ${C.border}`, borderRadius:'10px',
                fontSize:'13px', fontWeight:'500', color:C.textSec,
                cursor:'pointer', textAlign:'center' }}>
              {d.label}
            </button>
          ))}
        </div>
        <button onClick={onCancel}
          style={{ width:'100%', padding:'10px', background:'transparent',
            border:`1px solid ${C.border}`, borderRadius:'8px',
            fontSize:'12px', color:C.textMuted, cursor:'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── chart option builder ─────────────────────────────────────────────────────

const buildTrendOption = (data, period, multiYear) => {
  const labelFmt = (v) => {
    try {
      const d = parseLocalDate(v);
      if (!d) return v;
      if (period === 'monthly') {
        const mmm = d.toLocaleDateString('en-GB', { month:'short' });
        if (multiYear && d.getMonth() === 0) return mmm + '\n' + d.getFullYear();
        return mmm;
      }
      const label = d.toLocaleDateString('en-GB', { day:'numeric', month:'short' });
      if (multiYear && d.getDate() <= 7 && d.getMonth() === 0) return label + '\n' + d.getFullYear();
      return label;
    } catch { return v; }
  };

  const tipFmt = (p) => {
    try {
      const v = p[0].value ?? 0;
      const lbl = period === 'monthly'
        ? fmtDate(p[0].name, { month:'short', year:'numeric' })
        : fmtDate(p[0].name, { day:'numeric', month:'short', year: multiYear ? 'numeric' : undefined });
      return `${lbl}<br/><b>${Number(v).toLocaleString()}</b>`;
    } catch { return p[0].name; }
  };

  const markAreaData = (() => {
    if (!multiYear || !data.length) return [];
    const yearGroups = {};
    data.forEach(r => {
      const yr = String(r.period).slice(0, 4);
      if (!yearGroups[yr]) yearGroups[yr] = { start: r.period, end: r.period };
      yearGroups[yr].end = r.period;
    });
    return Object.entries(yearGroups).map(([yr, { start, end }], i) => [
      { xAxis: start, itemStyle: { color: i % 2 === 0 ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.04)' }, label: { show:false } },
      { xAxis: end }
    ]);
  })();

  const rawISO   = data.map(d => toISOKey(d.period));
  const valueMap = {};
  data.forEach(d => { valueMap[toISOKey(d.period)] = d.value ?? 0; });

  const filledISO = generateFilledPeriodKeys(rawISO, period);

  return {
    backgroundColor: 'transparent',
    grid: { left:8, right:8, top:8, bottom:8, containLabel:true },
    tooltip: {
      trigger: 'axis',
      backgroundColor: C.bgElevated,
      borderColor: C.border,
      textStyle: { color: C.textPrimary, fontSize: 11 },
      formatter: tipFmt,
    },
    xAxis: {
      type: 'category',
      data: filledISO,
      axisLine: { lineStyle: { color: C.border } },
      axisTick: { show: false },
      axisLabel: { color:C.textMuted, fontSize:10, formatter: labelFmt, rotate: multiYear ? 30 : 0, interval: 'auto', hideOverlap: true },
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: C.bgElevated } },
      axisLabel: { color:C.textMuted, fontSize:10, formatter: v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v },
    },
    series: [{
      type: 'line', smooth: true,
      data: filledISO.map(k => valueMap[k] ?? 0),
      lineStyle: { color: C.accent, width: 2.5 },
      itemStyle: { color: C.accent },
      symbol: 'circle', symbolSize: filledISO.length > 60 ? 2 : 4,
      showSymbol: filledISO.length <= 5,
      areaStyle: { color: { type:'linear', x:0, y:0, x2:0, y2:1, colorStops:[{ offset:0, color:'rgba(168,224,99,0.2)' },{ offset:1, color:'rgba(168,224,99,0)' }] } },
      markArea: markAreaData.length ? { silent:true, data: markAreaData } : undefined,
      markLine: period === 'daily' ? {
        silent: true, symbol: 'none',
        data: [{ yAxis: 0 }],
        lineStyle: { color: 'rgba(156,163,175,0.35)', width: 1, type: 'solid' },
        label: { show: false },
      } : undefined,
    }],
  };
};

// ─── main component ───────────────────────────────────────────────────────────

const SalesOverview = () => {

  // ── date / period state ───────────────────────────────────────────────────
  const [activePeriod, setAP]   = useState(() => localStorage.getItem('ccs_period') || 'this_month');
  const setPeriod = (p) => { localStorage.setItem('ccs_period', p); setAP(p); };
  // ── Branch multi-select (boss + area_manager only, persists in localStorage) ─
  const [branches,        setBranches]    = useState([]);
  const [selectedBranches, setSelected]  = useState(() => {
    try { return JSON.parse(localStorage.getItem('ccs_branch') || '[]'); } catch { return []; }
  });
  const [pendingBranches, setPending] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ccs_branch') || '[]'); } catch { return []; }
  });
  const [showBranchDrop, setShowBranchDrop] = useState(false);
  const openBranchDrop = () => { if (showBranchDrop) { setShowBranchDrop(false); } else { setPending(selectedBranches); setShowBranchDrop(true); } };
  const toggleBranch   = (code) => setPending(prev => prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]);
  const applyBranches  = () => { localStorage.setItem('ccs_branch', JSON.stringify(pendingBranches)); setSelected(pendingBranches); setShowBranchDrop(false); };
  const resetBranches  = () => { setPending([]); setSelected([]); localStorage.setItem('ccs_branch', '[]'); setShowBranchDrop(false); };

  // Load available branches for this tenant
  useEffect(() => {
    import('../../services/api').then(({ default: api }) => {
      api.get('/api/branches').then(r => setBranches(r.data.branches || [])).catch(() => {});
    });
  }, []);

    // Compute date range synchronously from period string
  const _computeDates = (period) => {
    const now = new Date();
    const _p  = n => String(n).padStart(2, '0');
    const _f  = d => `${d.getFullYear()}-${_p(d.getMonth()+1)}-${_p(d.getDate())}`;
    if (period === 'yesterday')      { const y = new Date(now); y.setDate(y.getDate()-1); return [_f(y), _f(y)]; }
    if (period === 'this_month')     return [_f(new Date(now.getFullYear(), now.getMonth(), 1)), _f(now)];
    if (period === 'this_week')      { const d = new Date(now); const day = d.getDay()||7; d.setDate(d.getDate()-day+1); const e = new Date(d); e.setDate(d.getDate()+6); return [_f(d), _f(e)]; }
    if (period === 'this_year')      return [_f(new Date(now.getFullYear(), 0, 1)), _f(now)];
    if (period === 'last_year')      return [_f(new Date(now.getFullYear()-1, 0, 1)), _f(new Date(now.getFullYear()-1, 11, 31))];
    if (period === 'last_month')     return [_f(new Date(now.getFullYear(), now.getMonth()-1, 1)), _f(new Date(now.getFullYear(), now.getMonth(), 0))];
    if (period === 'last_week')      { const d = new Date(now); const day = d.getDay()||7; d.setDate(d.getDate()-day-6); const e = new Date(d); e.setDate(d.getDate()+6); return [_f(d), _f(e)]; }
    if (period === 'last_year_month'){ const m = now.getMonth(), ly = now.getFullYear()-1; return [_f(new Date(ly,m,1)), _f(new Date(ly,m+1,0))]; }
    if (period === 'custom')        return [localStorage.getItem('ccs_from')||'', localStorage.getItem('ccs_to')||''];
    return ['', ''];
  };
  const _initPeriod = localStorage.getItem('ccs_period') || 'this_month';
  const [_initFrom, _initTo] = _computeDates(_initPeriod);
  const _initPendFrom = _initPeriod === 'custom' ? (localStorage.getItem('ccs_from')||'') : '';
  const _initPendTo   = _initPeriod === 'custom' ? (localStorage.getItem('ccs_to')||'')   : '';
const [dateFrom, setDateFrom] = useState(_initFrom);
  const [dateTo,   setDateTo]   = useState(_initTo);
  const [pendingFrom, setPF]    = useState(_initPendFrom);
  const [pendingTo,   setPT]    = useState(_initPendTo);

  // ── filter state ──────────────────────────────────────────────────────────
  const [filtersOpen,  setFO]        = useState(false);
  const _initFilters = () => { try { return JSON.parse(localStorage.getItem('ccs_filters') || '{}'); } catch { return {}; } };
  const _sf = _initFilters();
  const [filterOpts,   setFilterOpts] = useState({ staff:[], classify:[], classify_a:[], classify_b:[], products:[], payment_modes:[] });
  const [activeFilters, setAF]       = useState({ staff: _sf.staff || [], classify: _sf.classify || [], classify_a: _sf.classify_a || [], classify_b: _sf.classify_b || [], product: _sf.product || [], payment_mode: _sf.payment_mode || [] });
  const applyFilters = (f) => { localStorage.setItem('ccs_filters', JSON.stringify(f)); setAF(f); };

  // ── kpi state ─────────────────────────────────────────────────────────────
  const [kpis,    setKpis]   = useState(null);
  const [loading, setLoading] = useState(true);

  // ── CCS BI breakdown chart state ──────────────────────────────────────────
  const [bySaleGroup,  setBySaleGroup]  = useState([]);
  const [byAgeGroup,   setByAgeGroup]   = useState([]);
  const [byClassifyA,  setByClassifyA]  = useState([]);
  const [byClassifyB,  setByClassifyB]  = useState([]);
  const [byStaff,      setByStaff]      = useState([]); // kept for data loading

  // Pie chart refs for interactive legend dispatch
  const ageRef      = useRef(null);
  const classifyARef = useRef(null);
  const classifyBRef = useRef(null);

  // Bar click-to-highlight state
  const [selSaleGroupBar, setSelSaleGroupBar] = useState(null);

  // ── trend state ───────────────────────────────────────────────────────────
  const [revTrend,  setRevTrend]  = useState([]);
  const [custTrend, setCustTrend] = useState([]);
  const [txnTrend,  setTxnTrend]  = useState([]);
  const [avgTrend,  setAvgTrend]  = useState([]);
  const [trendPeriod, setTP]        = useState(() => localStorage.getItem('ccs_gran') || 'daily');
  const setGran = (g) => { localStorage.setItem('ccs_gran', g); setTP(g); };

  // Force monthly for All Time
  useEffect(() => {
    if (activePeriod === 'all' && trendPeriod !== 'monthly') setGran('monthly');
  }, [activePeriod]);
  const [availableYears, setAY]     = useState([]);
  const [selectedYears, setSY]      = useState([]);
  const [comparisonData, setCD]     = useState({}); // { year: [{period, value}] }
  const [trendLoading, setTrendLoading] = useState(false);
  const [compLoading, setCL]        = useState(false);

  // ── drill state ───────────────────────────────────────────────────────────
  // drillStack: [{dim, ctx, clickedLabel}] — last entry = current view
  // Default start: 'classify' (Group) as requested
  const [drillData,        setDD]          = useState([]);
  const [drillStack,       setDrillStack]  = useState([{ dim: 'classify', ctx: {}, clickedLabel: null }]);
  const [drillMetric,      setDM]          = useState('revenue');
  const [drillPickerOpen,  setPickerOpen]  = useState(false);
  const [pendingDrillItem, setPendingItem] = useState(null);

  // ── collapse state ────────────────────────────────────────────────────────
  const [collapsed, setCollapsed] = useState({});

  // ── effects ───────────────────────────────────────────────────────────────

  // One-time mount: load visual prefs
  useEffect(() => {
    getVisualPrefs().then(r => setCollapsed(r.data || {})).catch(() => {});
  }, []);

  // Reload filter options whenever date or branch changes
  useEffect(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    if (selectedBranches.length) p.branch_code = selectedBranches.join(',');
    getSalesFilterOptions(p).then(r => setFilterOpts(r.data || {})).catch(() => {});
  }, [dateFrom, dateTo, selectedBranches]);

  // Set date range when quick filter changes
  useEffect(() => {
    if (activePeriod === 'custom') return;
    const now = new Date();
    const _p = n => String(n).padStart(2, '0');
    const _f = d => `${d.getFullYear()}-${_p(d.getMonth()+1)}-${_p(d.getDate())}`;
    let from = '', to = '';
    if (activePeriod === 'yesterday') {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      from = to = _f(y);
    } else if (activePeriod === 'this_month') {
      from = _f(new Date(now.getFullYear(), now.getMonth(), 1));
      to   = _f(now);
    } else if (activePeriod === 'this_week') {
      const d = new Date(now);
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day + 1);
      const e = new Date(d); e.setDate(d.getDate() + 6);
      from = _f(d);
      to   = _f(e);
    } else if (activePeriod === 'this_year') {
      from = _f(new Date(now.getFullYear(), 0, 1));
      to   = _f(now);
    } else if (activePeriod === 'last_year') {
      from = _f(new Date(now.getFullYear() - 1, 0, 1));
      to   = _f(new Date(now.getFullYear() - 1, 11, 31));
    } else if (activePeriod === 'last_month') {
      from = _f(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      to   = _f(new Date(now.getFullYear(), now.getMonth(), 0));
    } else if (activePeriod === 'last_week') {
      const d = new Date(now);
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day - 6); // last Monday
      const e = new Date(d); e.setDate(d.getDate() + 6); // last Sunday
      from = _f(d);
      to   = _f(e);
    } else if (activePeriod === 'last_year_month') {
      const m = now.getMonth();
      const ly = now.getFullYear() - 1;
      from = _f(new Date(ly, m, 1));
      to   = _f(new Date(ly, m + 1, 0));
    }
    // 'all' → from/to stay '' → no date filter sent to API
    setDateFrom(from);
    setDateTo(to);
  }, [activePeriod]);

  // Reload everything when dates or applied filters change
  useEffect(() => { loadAll(); }, [dateFrom, dateTo, activeFilters, selectedBranches]);

  // Reload drill when metric changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const { dim, ctx } = drillStack[drillStack.length - 1];
    fetchDrill(dim, ctx, drillMetric).then(setDD).catch(() => {});
  }, [drillMetric]);

  // Reload trends when period changes (only when no filters active)
  useEffect(() => {
    if (activeFiltersCount > 0) return;
    loadTrends(trendPeriod);
  }, [trendPeriod]);

  // ── params builders ───────────────────────────────────────────────────────

  const globalFilterParams = useCallback(() => {
    const p = {};
    if (activeFilters.staff.length)        p.staff        = activeFilters.staff.join(',');
    if (activeFilters.classify.length)     p.classify     = activeFilters.classify.join(',');
    if (activeFilters.classify_a.length)   p.classify_a   = activeFilters.classify_a.join(',');
    if (activeFilters.classify_b.length)   p.classify_b   = activeFilters.classify_b.join(',');
    if (activeFilters.product.length)      p.product      = activeFilters.product.join(',');
    if (activeFilters.payment_mode.length) p.payment_mode = activeFilters.payment_mode.join(',');
    return p;
  }, [activeFilters]);

  const dateParams = useCallback(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    if (selectedBranches.length > 0) p.branch_code = selectedBranches.join(',');
    return p;
  }, [dateFrom, dateTo, selectedBranches]);

  const allParams = useCallback(() => ({
    ...dateParams(),
    ...globalFilterParams(),
  }), [dateParams, globalFilterParams]);

  // ── data loaders ──────────────────────────────────────────────────────────

  const loadAll = async () => {
    setLoading(true);
    const startDim = 'classify'; // default start dim
    const [overviewRes, drillRes] = await Promise.allSettled([
      getSalesOverview(allParams()),
      getSalesDrill({ ...allParams(), group_by: startDim, metric: drillMetric }),
    ]);
    if (overviewRes.status === 'fulfilled') {
      const d = overviewRes.value.data;
      setKpis(d);
      setBySaleGroup(d.by_sale_group  || []);
      setByAgeGroup(d.by_age_group    || []);
      setByClassifyA(d.by_classify_a  || []);
      setByClassifyB(d.by_classify_b  || []);
      setByStaff(d.by_staff          || []);
    } else {
      console.error('overview error:', overviewRes.reason);
    }
    if (drillRes.status === 'fulfilled') {
      setDD(drillRes.value.data.data || []);
      setDrillStack([{ dim: startDim, ctx: {}, clickedLabel: null }]);
    } else {
      console.error('drill error:', drillRes.reason);
    }
    setLoading(false);
    loadTrends(trendPeriod);
  };

  const loadTrends = async (period = 'monthly') => {
    setTrendLoading(true);
    try {
      const p = { period, ...dateParams() };
      if (activeFilters.staff.length)        p.staff        = activeFilters.staff.join(',');
      if (activeFilters.classify.length)     p.classify     = activeFilters.classify.join(',');
      if (activeFilters.classify_a.length)   p.classify_a   = activeFilters.classify_a.join(',');
      if (activeFilters.classify_b.length)   p.classify_b   = activeFilters.classify_b.join(',');
      if (activeFilters.product.length)      p.product      = activeFilters.product.join(',');
      if (activeFilters.payment_mode.length) p.payment_mode = activeFilters.payment_mode.join(',');
      const [r1, r2, r3, r4] = await Promise.all([
        getSalesTrend({ ...p, metric: 'revenue'      }),
        getSalesTrend({ ...p, metric: 'customers'    }),
        getSalesTrend({ ...p, metric: 'transactions' }),
        getSalesAvgValueTrend(p).catch(()=>({ data:{ data:[] } })),
      ]);
      setRevTrend(r1.data.data);
      setCustTrend(r2.data.data);
      setTxnTrend(r3.data.data);
      setAvgTrend(r4.data.data || []);
    } catch(e) { console.error(e); }
    finally { setTrendLoading(false); }
  };

  // Derive available years from revenue trend data (full data range, fetched once)
  const [allAvailableYears, setAllAY] = useState([]);

  // Keep a ref to loadComparison so the mount effect always calls the latest version
  // (avoids stale-closure issues when selectedYears is set asynchronously)
  const loadComparisonRef = useRef(null);

  // loadComparison re-defines each render so it always closes over the latest state
  const loadComparison = async (years) => {
    if (!years.length) return;
    setCL(true);
    try {
      const startMonth = dateFrom ? parseInt(dateFrom.split('-')[1]) : 1;
      const endMonth   = dateTo   ? parseInt(dateTo.split('-')[1])   : 12;

      const extraParams = {};
      if (selectedBranches.length > 0)       extraParams.branch_code  = selectedBranches.join(',');
      if (activeFilters.staff.length)        extraParams.staff        = activeFilters.staff.join(',');
      if (activeFilters.classify.length)     extraParams.classify     = activeFilters.classify.join(',');
      if (activeFilters.classify_a.length)   extraParams.classify_a   = activeFilters.classify_a.join(',');
      if (activeFilters.classify_b.length)   extraParams.classify_b   = activeFilters.classify_b.join(',');
      if (activeFilters.product.length)      extraParams.product      = activeFilters.product.join(',');
      if (activeFilters.payment_mode.length) extraParams.payment_mode = activeFilters.payment_mode.join(',');

      const results = await Promise.all(years.map(yr => {
        const sm = isNaN(startMonth) ? 1  : startMonth;
        const em = isNaN(endMonth)   ? 12 : endMonth;
        const from = `${yr}-${String(sm).padStart(2,'0')}-01`;
        const lastDay = new Date(parseInt(yr), em, 0).getDate();
        const to   = `${yr}-${String(em).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`;
        return getSalesTrend({ period:'monthly', date_from:from, date_to:to, metric:'revenue', ...extraParams })
          .then(r => ({ yr, data: r.data.data || [] }))
          .catch(() => ({ yr, data: [] }));
      }));
      const map = {};
      results.forEach(({ yr, data }) => { map[yr] = data; });
      setCD(map);
    } catch(e) { console.error(e); }
    finally { setCL(false); }
  };

  // Always keep the ref current so the mount effect below can call the latest version
  loadComparisonRef.current = loadComparison;

  useEffect(() => {
    // Fetch all years once on mount; directly trigger comparison so it loads on first render
    getSalesTrend({ period: 'monthly', metric: 'revenue' })
      .then(r => {
        const years = [...new Set((r.data.data||[]).map(d => String(d.period).slice(0,4)))].sort();
        setAllAY(years);
        if (years.length > 0) {
          const initYears = years.slice(-2);
          setSY(initYears);
          // Call via ref so we always get the version with the current state in closure
          loadComparisonRef.current(initYears);
        }
      }).catch(()=>{});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When revTrend loads (filtered), keep availableYears from allAvailableYears
  useEffect(() => {
    if (!revTrend.length) return;
    const years = [...new Set(revTrend.map(r => String(r.period).slice(0,4)))].sort();
    setAY(years);
  }, [revTrend]);

  // Re-run comparison whenever years selection or filters/dates change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedYears.length === 0) { setCD({}); return; }
    loadComparison(selectedYears);
  }, [selectedYears, activeFilters, selectedBranches, dateFrom, dateTo]);

  // ── collapse toggle ───────────────────────────────────────────────────────

  const toggleCollapse = useCallback((key) => {
    const next = !collapsed[key];
    setCollapsed(prev => ({ ...prev, [key]: next }));
    setVisualPref({ visual_key: key, is_collapsed: next }).catch(() => {});
  }, [collapsed]);

  // ── custom date apply ─────────────────────────────────────────────────────

  const applyCustom = () => {
    localStorage.setItem('ccs_from', pendingFrom);
    localStorage.setItem('ccs_to', pendingTo);
    setDateFrom(pendingFrom);
    setDateTo(pendingTo);
  };

  // ── drill helpers ────────────────────────────────────────────────────────

  // Build API params for a drill call
  const fetchDrill = useCallback(async (dim, ctx, metric) => {
    const p = { ...allParams(), group_by: dim, metric: metric || drillMetric };
    if (ctx.payment_mode) p.ctx_payment_mode = ctx.payment_mode;
    if (ctx.classify)     p.ctx_classify     = ctx.classify;
    if (ctx.age_group)    p.ctx_age_group    = ctx.age_group;
    if (ctx.product_name) p.ctx_product_name = ctx.product_name;
    if (ctx.sold_code)    p.ctx_sold_code    = ctx.sold_code;
    const res = await getSalesDrill(p);
    return res.data.data || [];
  }, [allParams]);

  // Reset drill to a chosen starting dimension
  const resetDrill = useCallback(async (dim) => {
    try {
      const data = await fetchDrill(dim, {}, drillMetric);
      setDD(data);
      setDrillStack([{ dim, ctx: {}, clickedLabel: null }]);
    } catch(e) { console.error(e); }
    setPickerOpen(false);
    setPendingItem(null);
  }, [fetchDrill, drillMetric]);

  // Called when user picks a dimension from the bottom-sheet picker
  const drillDown = useCallback(async (chosenDim) => {
    if (!pendingDrillItem) return;
    const item   = pendingDrillItem;
    const curDim = drillStack[drillStack.length - 1].dim;
    const curCtx = drillStack[drillStack.length - 1].ctx;
    const newCtx = { ...curCtx, [curDim]: item.key };
    try {
      const data = await fetchDrill(chosenDim, newCtx, drillMetric);
      setDD(data);
      setDrillStack(prev => [
        ...prev,
        { dim: chosenDim, ctx: newCtx, clickedLabel: item.label || item.key },
      ]);
    } catch(e) { console.error(e); }
    setPickerOpen(false);
    setPendingItem(null);
  }, [pendingDrillItem, drillStack, fetchDrill, drillMetric]);

  // Navigate back to a previous stack entry via breadcrumb
  const drillBack = useCallback(async (stackIndex) => {
    const entry = drillStack[stackIndex];
    try {
      const data = await fetchDrill(entry.dim, entry.ctx, drillMetric);
      setDD(data);
      setDrillStack(prev => prev.slice(0, stackIndex + 1));
    } catch(e) { console.error(e); }
  }, [drillStack, fetchDrill, drillMetric]);

  // ── chart options ─────────────────────────────────────────────────────────

  const revOption  = buildTrendOption(revTrend,  trendPeriod, isMultiYear(revTrend));
  const custOption = buildTrendOption(custTrend, trendPeriod, isMultiYear(custTrend));
  const txnOption  = buildTrendOption(txnTrend,  trendPeriod, isMultiYear(txnTrend));
  const avgOption  = buildTrendOption(
    avgTrend.map(d => ({ period: d.period, value: d.avg_value ?? 0 })),
    trendPeriod,
    isMultiYear(avgTrend)
  );

  // ── Period Comparison chart option (monthly, multi-year lines)
  const YEAR_COLORS = [C.accent, C.blue, C.purple, C.amber, '#FF6B6B', '#34D399'];

  // Compute which month numbers are in the selected date range
  const selectedMonths = (() => {
    const sm = dateFrom ? parseInt(dateFrom.split('-')[1]) : 1;
    const em = dateTo   ? parseInt(dateTo.split('-')[1])   : 12;
    const months = [];
    for (let m = sm; m <= em; m++) months.push(m);
    return months;
  })();

  const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const comparisonOption = (() => {
    const years = Object.keys(comparisonData).sort();
    if (years.length === 0) return null;
    const displayMonths = selectedMonths.length > 0 ? selectedMonths : Array.from({length:12},(_,i)=>i+1);
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor:C.bgElevated, borderColor:C.border,
        textStyle:{ color:C.textPrimary, fontSize:11 },
        formatter: (params) => {
          const month = MONTH_LABELS[parseInt(params[0].name)-1] || params[0].name;
          return month + '<br/>' + params.map(p =>
            `<span style="color:${p.color}">●</span> ${p.seriesName}: <b>RM ${Number(p.value||0).toLocaleString(undefined,{maximumFractionDigits:0})}</b>`
          ).join('<br/>');
        },
      },
      legend:{ data:years, textStyle:{ color:C.textSec, fontSize:10 }, bottom:0, type:'scroll', pageIconColor: C.textSec, pageTextStyle: { color: C.textMuted, fontSize: 9 } },
      grid:{ left:8, right:8, top:8, bottom:32, containLabel:true },
      xAxis:{
        type:'category',
        data: displayMonths.map(m => String(m)),
        axisLine:{ lineStyle:{ color:C.border } }, axisTick:{ show:false },
        axisLabel:{ color:C.textMuted, fontSize:10, formatter: v => MONTH_LABELS[parseInt(v)-1]||v },
      },
      yAxis:{ type:'value', splitLine:{ lineStyle:{ color:C.bgElevated } }, axisLabel:{ color:C.textMuted, fontSize:10, formatter:v=>v>=1000?`${(v/1000).toFixed(0)}k`:v } },
      series: years.map((yr,i) => {
        const monthly = {};
        (comparisonData[yr]||[]).forEach(r => {
          const d = parseLocalDate(r.period);
          if (d) monthly[d.getMonth()+1] = r.value ?? 0;
        });
        return {
          name:yr, type:'line', smooth:true,
          data: displayMonths.map(m => monthly[m] ?? 0),
          lineStyle:{ color:YEAR_COLORS[i%YEAR_COLORS.length], width:2.5 },
          itemStyle:{ color:YEAR_COLORS[i%YEAR_COLORS.length] },
          symbol:'circle', symbolSize:5,
        };
      }),
    };
  })();

  // Derived from drillStack
  const currentDrillEntry = drillStack[drillStack.length - 1];
  const currentDim        = currentDrillEntry.dim;
  const usedDims          = drillStack.map(s => s.dim);
  const canDrillDeeper    = currentDim !== 'daily' && usedDims.length < DRILL_DIMS.length;

  // Breadcrumb path derived from stack
  const drillPath = drillStack.map((s, i) =>
    i === 0
      ? (DRILL_DIMS.find(d => d.key === s.dim)?.label || s.dim)
      : `${s.clickedLabel} → ${DRILL_DIMS.find(d => d.key === s.dim)?.label || s.dim}`
  );

  const drillDailyOption = currentDim === 'daily'
    ? buildTrendOption(
        drillData.map(d => ({ period: String(d.key), value: parseFloat(d.value) || 0 })),
        'daily',
        isMultiYear(drillData.map(d => ({ period: String(d.key) })))
      )
    : null;

  const isHorizontal = currentDim === 'product_name';
  const drillBarOption = (() => {
    if (currentDim === 'daily') return null;
    const labels = drillData.map(d => d.label || d.key || 'Unknown');
    const values = drillData.map(d => parseFloat(d.value) || 0);
    const colors = drillData.map((_, i) => DRILL_COLORS[i % DRILL_COLORS.length]);
    const yFmt   = v => drillMetric === 'qty' ? v : (v >= 1000 ? `${(v/1000).toFixed(0)}k` : v);

    if (isHorizontal) {
      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis', axisPointer: { type:'shadow' },
          backgroundColor: C.bgElevated, borderColor: C.border,
          textStyle: { color:C.textPrimary, fontSize:11 },
          formatter: p => `${p[0].name}<br/><b>${Number(p[0].value).toLocaleString()}</b>`,
        },
        grid: { left:8, right:30, top:8, bottom:8, containLabel:true },
        xAxis: {
          type:'value',
          splitLine: { lineStyle:{ color:C.bgElevated } },
          axisLabel: { color:C.textMuted, fontSize:9, formatter: yFmt },
        },
        yAxis: {
          type:'category', data:labels, inverse:true,
          axisLabel: { color:C.textMuted, fontSize:9, width:120, overflow:'truncate' },
          axisLine: { lineStyle:{ color:C.border } },
          axisTick: { show:false },
        },
        series: [{
          type:'bar',
          data: values.map((v, i) => ({ value:v, itemStyle:{ color:colors[i], borderRadius:[0,6,6,0] } })),
          emphasis: { itemStyle:{ opacity:0.8 } },
          label: { show:true, position:'right', color:C.textMuted, fontSize:9, formatter: p => Number(p.value).toLocaleString() },
        }],
      };
    }

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis', axisPointer:{ type:'shadow' },
        backgroundColor:C.bgElevated, borderColor:C.border,
        textStyle:{ color:C.textPrimary, fontSize:11 },
        formatter: p => `${p[0].name}<br/><b>${Number(p[0].value).toLocaleString()}</b>`,
      },
      grid: { left:8, right:8, top:8, bottom: labels.length > 6 ? 50 : 30, containLabel:true },
      xAxis: {
        type:'category', data:labels,
        axisLine:{ lineStyle:{ color:C.border } },
        axisTick:{ show:false },
        axisLabel:{ color:C.textMuted, fontSize:9, rotate:labels.length > 5 ? 30 : 0, interval:0, overflow:'truncate', width:60 },
      },
      yAxis: {
        type:'value',
        splitLine:{ lineStyle:{ color:C.bgElevated } },
        axisLabel:{ color:C.textMuted, fontSize:10, formatter:yFmt },
      },
      series: [{
        type:'bar',
        data: values.map((v, i) => ({ value:v, itemStyle:{ color:colors[i], borderRadius:[6,6,0,0] } })),
        emphasis:{ itemStyle:{ opacity:0.8 } },
      }],
    };
  })();

  // ── CCS BI chart options ──────────────────────────────────────────────────

  const CCSBI_COLORS = [C.accent, C.blue, C.purple, C.amber, '#FF6B6B', '#34D399', '#F472B6', '#38BDF8', '#FB923C', '#4ADE80', '#94A3B8'];

  const makePieOption = (data, labelKey, valueKey) => {
    const filtered = data.filter(r => parseFloat(r[valueKey] || 0) > 0);
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger:'item', backgroundColor:C.bgElevated, borderColor:C.border, textStyle:{ color:C.textPrimary, fontSize:11 },
        formatter: p => `${p.name}<br/>RM ${Number(p.value).toLocaleString(undefined,{maximumFractionDigits:0})}<br/>${p.percent}%` },
      legend: { show: false, data: filtered.map(r => String(r[labelKey] || 'Unknown')) },
      series: [{
        type:'pie', radius:['38%','65%'], center:['50%','50%'],
        data: filtered.map((r, i) => ({
          name: String(r[labelKey] || 'Unknown'),
          value: parseFloat(r[valueKey] || 0),
          itemStyle: { color: CCSBI_COLORS[i % CCSBI_COLORS.length] },
        })),
        label: { show:false },
      }],
    };
  };

  const makeSimpleLegend = (data, labelKey, valueKey) => {
    const total = data.reduce((s, r) => s + parseFloat(r[valueKey] || 0), 0);
    return data.map((r, i) => ({
      label: String(r[labelKey] || 'Unknown'),
      value: parseFloat(r[valueKey] || 0),
      pct:   total > 0 ? (parseFloat(r[valueKey] || 0) / total * 100).toFixed(1) : '0.0',
      color: CCSBI_COLORS[i % CCSBI_COLORS.length],
    }));
  };

  // Derive items/colors for interactive pie legends
  const ageItems      = byAgeGroup .filter(r => parseFloat(r.revenue||0) > 0).map(r => String(r.age_group  ||'Unknown'));
  const classifyAItems = byClassifyA.filter(r => parseFloat(r.revenue||0) > 0).map(r => String(r.classify_a||'Unknown'));
  const classifyBItems = byClassifyB.filter(r => parseFloat(r.revenue||0) > 0).map(r => String(r.classify_b||'Unknown'));
  const ageColors      = ageItems.map((_,i)      => CCSBI_COLORS[i%CCSBI_COLORS.length]);
  const classifyAColors = classifyAItems.map((_,i) => CCSBI_COLORS[i%CCSBI_COLORS.length]);
  const classifyBColors = classifyBItems.map((_,i) => CCSBI_COLORS[i%CCSBI_COLORS.length]);

  // eslint-disable-next-line
  const ageLegend      = useChartLegend(ageItems);
  // eslint-disable-next-line
  const classifyALegend = useChartLegend(classifyAItems);
  // eslint-disable-next-line
  const classifyBLegend = useChartLegend(classifyBItems);

  // Sales Group — bar (handles negatives correctly, with click-to-highlight)
  const saleGroupBarOption = (() => {
    const sorted = [...bySaleGroup].sort((a, b) => parseFloat(b.revenue) - parseFloat(a.revenue));
    const labels = sorted.map(r => String(r.sale_group || 'Unknown'));
    const values = sorted.map(r => parseFloat(r.revenue || 0));
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger:'axis', axisPointer:{type:'shadow'}, backgroundColor:C.bgElevated, borderColor:C.border,
        textStyle:{color:C.textPrimary, fontSize:11},
        formatter: p => `${p[0].name}<br/>RM <b>${Number(p[0].value).toLocaleString(undefined,{maximumFractionDigits:0})}</b>` },
      grid: { left:8, right:8, top:8, bottom:8, containLabel:true },
      xAxis: { type:'category', data:labels, axisLine:{lineStyle:{color:C.border}}, axisTick:{show:false},
        axisLabel:{color:C.textMuted, fontSize:9, rotate:labels.length > 5 ? 30 : 0, interval:0, overflow:'truncate', width:70} },
      yAxis: { type:'value', splitLine:{lineStyle:{color:C.bgElevated}},
        axisLabel:{color:C.textMuted, fontSize:10, formatter:v=>v>=1000?`${(v/1000).toFixed(0)}k`:v>=0?v:`-${Math.abs(v/1000).toFixed(0)}k`} },
      series:[{ type:'bar', data: values.map((v,i)=>({ value:v, itemStyle:{ color: v < 0 ? '#F87171' : CCSBI_COLORS[i%CCSBI_COLORS.length], borderRadius: v>=0?[6,6,0,0]:[0,0,6,6], opacity: selSaleGroupBar === null ? 1 : (selSaleGroupBar === i ? 1 : 0.3) } })) }],
    };
  })();

  const ageGroupPieOption  = makePieOption(byAgeGroup,  'age_group',  'revenue');
  const classifyAPieOption = makePieOption(byClassifyA, 'classify_a', 'revenue');
  const classifyBPieOption = makePieOption(byClassifyB, 'classify_b', 'revenue');

  const PieLegendRow = ({ items }) => (
    <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 12px', paddingTop:'6px' }}>
      {items.map((item, i) => (
        <div key={i} style={{ display:'flex', alignItems:'center', gap:'4px' }}>
          <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:item.color, flexShrink:0 }}/>
          <span style={{ fontSize:'10px', color:C.textSec }}>{item.label}</span>
          <span style={{ fontSize:'9px', color:C.textMuted }}>({item.pct}%)</span>
        </div>
      ))}
    </div>
  );

  // ── render ────────────────────────────────────────────────────────────────

  const activeFiltersCount = Object.values(activeFilters).reduce((n, arr) => n + arr.length, 0);

  const periodToggle = activeFiltersCount === 0 ? (
    <div style={toggleGroup}>
      {['daily','weekly','monthly'].map(p => {
        const disabled = activePeriod === 'all' && p !== 'monthly';
        return (
          <button key={p} style={{ ...toggleBtn(trendPeriod === p), opacity: disabled ? 0.4 : 1, cursor: disabled ? 'default' : 'pointer' }}
            onClick={() => !disabled && setGran(p)}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        );
      })}
    </div>
  ) : null;

  const RefreshBadge = () => trendLoading ? (
    <div style={{ display:'flex', alignItems:'center', gap:'4px', marginBottom:'4px' }}>
      <div style={{ width:'10px', height:'10px', border:`2px solid ${C.bgElevated}`, borderTopColor:C.accent, borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
      <span style={{ fontSize:'10px', color:C.textMuted }}>Updating…</span>
    </div>
  ) : null;

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'60vh' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:'40px', height:'40px', border:`3px solid ${C.bgElevated}`, borderTopColor:C.accent, borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 12px' }}/>
        <p style={{ color:C.textMuted, fontSize:'13px' }}>Loading sales data...</p>
      </div>
    </div>
  );

  return (
    <div style={{ padding:'16px', paddingBottom:'32px' }}>

      {/* ── QUICK FILTERS ── */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
        {QUICK.map(q => (
          <button key={q.id} onClick={() => setPeriod(q.id)} style={filterPill(activePeriod === q.id)}>
            {q.label}
          </button>
        ))}
      </div>

          {/* Branch multi-select — boss, area_manager, superadmin only */}
          {(() => {
            try {
              const u = JSON.parse(localStorage.getItem('user') || '{}');
              if (!['boss','area_manager','superadmin'].includes(u.role)) return null;
            } catch { return null; }
            if (branches.length === 0) return null;
            const label = selectedBranches.length === 0 ? 'All Branches'
              : selectedBranches.length === 1 ? (branches.find(b => b.code === selectedBranches[0])?.name || selectedBranches[0])
              : `${selectedBranches.length} Branches`;
            return (
              <div style={{ marginBottom:'10px', position:'relative', display:'inline-block' }}>
                <button onClick={openBranchDrop}
                  style={{ ...filterPill(selectedBranches.length > 0), display:'flex', alignItems:'center', gap:'6px', padding:'6px 14px' }}>
                  🏢 {label} <span style={{ fontSize:'10px', opacity:0.7 }}>{showBranchDrop ? '▴' : '▾'}</span>
                </button>
                {showBranchDrop && (
                  <div style={{ position:'absolute', top:'38px', left:0, zIndex:999,
                    background:C.bgElevated, border:`1px solid ${C.border}`, borderRadius:'12px',
                    padding:'8px', minWidth:'180px', boxShadow:'0 8px 32px rgba(0,0,0,0.4)' }}>
                    {branches.map(b => (
                      <label key={b.code} style={{ display:'flex', alignItems:'center', gap:'8px',
                        padding:'7px 10px', cursor:'pointer', borderRadius:'8px', fontSize:'13px',
                        color: pendingBranches.includes(b.code) ? C.accent : C.textSec,
                        background: pendingBranches.includes(b.code) ? 'rgba(168,224,99,0.1)' : 'transparent' }}>
                        <input type="checkbox" checked={pendingBranches.includes(b.code)}
                          onChange={() => toggleBranch(b.code)} style={{ accentColor: C.accent }} />
                        {b.name || b.code}
                      </label>
                    ))}
                    <div style={{ borderTop:`1px solid ${C.border}`, marginTop:'6px', paddingTop:'6px', display:'flex', flexDirection:'column', gap:'4px' }}>
                      <button onClick={applyBranches}
                        style={{ width:'100%', background:C.accent, border:'none', borderRadius:'6px',
                          color:'#000', fontSize:'12px', fontWeight:'700', cursor:'pointer', padding:'6px 0' }}>
                        Apply
                      </button>
                      <button onClick={() => { resetBranches(); setShowBranchDrop(false); }}
                        style={{ width:'100%', background:'transparent', border:'none',
                          color:C.textMuted, fontSize:'12px', cursor:'pointer', padding:'4px 0' }}>
                        ↩ Reset to All
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

      {/* ── CUSTOM DATE RANGE ── */}
      {activePeriod === 'custom' && (
        <div style={{ display:'flex', gap:'8px', marginBottom:'12px', alignItems:'flex-end' }}>
          {[['From', pendingFrom, setPF], ['To', pendingTo, setPT]].map(([label, val, setter]) => (
            <div key={label} style={{ flex:1 }}>
              <p style={{ fontSize:'10px', color:C.textMuted, marginBottom:'4px' }}>{label}</p>
              <input
                type="date"
                value={val}
                onChange={e => setter(e.target.value)}
                style={{ width:'100%', padding:'8px 10px', background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'8px', fontSize:'12px', color: val ? C.textPrimary : C.textMuted, outline:'none', boxSizing:'border-box' }}
              />
            </div>
          ))}
          <button
            onClick={applyCustom}
            disabled={!pendingFrom || !pendingTo}
            style={{ padding:'8px 14px', background: pendingFrom && pendingTo ? C.accent : C.bgElevated, color: pendingFrom && pendingTo ? '#000' : C.textMuted, border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'600', cursor: pendingFrom && pendingTo ? 'pointer' : 'default', flexShrink:0 }}
          >
            Apply
          </button>
        </div>
      )}

      {/* ── ADVANCED FILTERS PANEL ── */}
      <div style={{ marginBottom:'12px' }}>
        <button
          onClick={() => setFO(o => !o)}
          style={{ ...filterPill(activeFiltersCount > 0), display:'flex', alignItems:'center', gap:'6px' }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M7 12h10M11 18h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          Filters{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}
          <span style={{ fontSize:'9px' }}>{filtersOpen ? '▲' : '▼'}</span>
        </button>
        {filtersOpen && (
          <div style={{ marginTop:'8px', padding:'12px', background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'12px' }}>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
              <MultiSelect label="Staff"         options={(filterOpts.staff || []).map(s => ({ key: s.code, label: s.name }))}  selected={activeFilters.staff}        onChange={v => applyFilters({...activeFilters, staff:v})} />
              <MultiSelect label="Classify"      options={(filterOpts.classify || [])}       selected={activeFilters.classify}     onChange={v => applyFilters({...activeFilters, classify:v})} />
              <MultiSelect label="Classify A"    options={(filterOpts.classify_a || [])}     selected={activeFilters.classify_a}   onChange={v => applyFilters({...activeFilters, classify_a:v})} />
              <MultiSelect label="Classify B"    options={(filterOpts.classify_b || [])}     selected={activeFilters.classify_b}   onChange={v => applyFilters({...activeFilters, classify_b:v})} />
              <MultiSelect label="Product"       options={(filterOpts.products || [])}       selected={activeFilters.product}      onChange={v => applyFilters({...activeFilters, product:v})} />
              <MultiSelect label="Payment Mode"  options={(filterOpts.payment_modes || [])}  selected={activeFilters.payment_mode} onChange={v => applyFilters({...activeFilters, payment_mode:v})} />
              <button
                onClick={() => applyFilters({ staff:[], classify:[], classify_a:[], classify_b:[], product:[], payment_mode:[] })}
                style={{ alignSelf:'flex-end', fontSize:'11px', color:C.danger, background:'none', border:`1px solid ${C.border}`, borderRadius:'6px', padding:'5px 10px', cursor:'pointer', whiteSpace:'nowrap' }}
              >
                Reset All
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── KPI GRID ── */}
      <CollapsibleSection visKey="kpi" title="Summary" collapsed={!!collapsed['kpi']} onToggle={toggleCollapse}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
          <KPICard title="Total Sales"         value={`RM ${(kpis?.total_sales_a||0).toLocaleString(undefined,{maximumFractionDigits:0})}`}       iconBg={C.accentDim} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6" stroke={C.accent} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="Total Collection"    value={`RM ${(kpis?.total_revenue||0).toLocaleString(undefined,{maximumFractionDigits:0})}`}        iconBg={C.blueDim}   icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke={C.blue} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="Total Bills"         value={(kpis?.total_bills_c||0).toLocaleString()}                   iconBg={C.amberDim}  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 5h6" stroke={C.amber} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="Visit Customers"     value={(kpis?.visit_customers_d||0).toLocaleString()}               iconBg={C.purpleDim} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8z" stroke={C.purple} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="Unique Customers"    value={(kpis?.unique_customers||0).toLocaleString()}                iconBg={C.accentDim} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke={C.accent} strokeWidth="2"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke={C.accent} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="Payment Ratio"     value={`${(kpis?.payment_ratio||0).toFixed(1)}%`}                   iconBg={C.blueDim}   icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>} />
          <KPICard title="Avg Spending"      value={`RM ${(kpis?.avg_spending_be||0).toLocaleString(undefined,{maximumFractionDigits:0})}`} iconBg={C.amberDim} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6" stroke={C.amber} strokeWidth="2" strokeLinecap="round"/></svg>} />
        </div>
      </CollapsibleSection>

      {/* ── COLLECTION BY SALES GROUP (bar — handles negatives) ── */}
      {bySaleGroup.length > 0 && (
        <CollapsibleSection visKey="so_sale_group" title="Collection by Sales Group" collapsed={!!collapsed['so_sale_group']} onToggle={toggleCollapse}>
          <ReactECharts option={saleGroupBarOption} style={{ height:'220px', width:'100%' }}
            onEvents={{ click: (p) => setSelSaleGroupBar(prev => prev === p.dataIndex ? null : p.dataIndex) }} />
          {selSaleGroupBar !== null && (
            <button onClick={() => setSelSaleGroupBar(null)}
              style={{ marginTop:'4px', fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.textMuted, cursor:'pointer' }}>
              Clear highlight
            </button>
          )}
        </CollapsibleSection>
      )}

      {/* ── COLLECTION BY AGE GROUP ── */}
      {byAgeGroup.length > 0 && (
        <CollapsibleSection visKey="so_age_group" title="Collection by Age Group" collapsed={!!collapsed['so_age_group']} onToggle={toggleCollapse}>
          <ReactECharts ref={ageRef} option={ageGroupPieOption} style={{ height:'180px', width:'100%' }} />
          <ChartLegend items={ageItems} colors={ageColors} chartRef={ageRef}
            selected={ageLegend.selected} onToggle={ageLegend.toggle} onSelectAll={ageLegend.selectAll} onReset={ageLegend.resetAll} />
        </CollapsibleSection>
      )}

      {/* ── COLLECTION BY CLASSIFY A ── */}
      {byClassifyA.length > 0 && (
        <CollapsibleSection visKey="so_classify_a" title="Collection by Classify A (New/Old)" collapsed={!!collapsed['so_classify_a']} onToggle={toggleCollapse}>
          <ReactECharts ref={classifyARef} option={classifyAPieOption} style={{ height:'180px', width:'100%' }} />
          <ChartLegend items={classifyAItems} colors={classifyAColors} chartRef={classifyARef}
            selected={classifyALegend.selected} onToggle={classifyALegend.toggle} onSelectAll={classifyALegend.selectAll} onReset={classifyALegend.resetAll} />
        </CollapsibleSection>
      )}

      {/* ── COLLECTION BY CLASSIFY B ── */}
      {byClassifyB.length > 0 && (
        <CollapsibleSection visKey="so_classify_b" title="Collection by Classify B (VIP/NON)" collapsed={!!collapsed['so_classify_b']} onToggle={toggleCollapse}>
          <ReactECharts ref={classifyBRef} option={classifyBPieOption} style={{ height:'180px', width:'100%' }} />
          <ChartLegend items={classifyBItems} colors={classifyBColors} chartRef={classifyBRef}
            selected={classifyBLegend.selected} onToggle={classifyBLegend.toggle} onSelectAll={classifyBLegend.selectAll} onReset={classifyBLegend.resetAll} />
        </CollapsibleSection>
      )}

      {/* ── REVENUE TREND ── */}
      <CollapsibleSection visKey="rev_trend" title="Revenue Trend" collapsed={!!collapsed['rev_trend']} onToggle={toggleCollapse} headerRight={periodToggle}>
        <RefreshBadge />
        <ReactECharts option={revOption} style={{ height:'200px' }} />
      </CollapsibleSection>

      {/* ── UNIQUE CUSTOMER VISITS TREND ── */}
      <CollapsibleSection visKey="cust_trend" title="Unique Customer Visits" collapsed={!!collapsed['cust_trend']} onToggle={toggleCollapse} headerRight={periodToggle}>
        <RefreshBadge />
        <ReactECharts option={custOption} style={{ height:'200px' }} />
      </CollapsibleSection>

      {/* ── TRANSACTIONS TREND ── */}
      <CollapsibleSection visKey="txn_trend" title="Transactions Trend" collapsed={!!collapsed['txn_trend']} onToggle={toggleCollapse} headerRight={periodToggle}>
        <RefreshBadge />
        <ReactECharts option={txnOption} style={{ height:'200px' }} />
      </CollapsibleSection>

      {/* ── AVERAGE SALE VALUE TREND ── */}
      <CollapsibleSection visKey="avg_trend" title="Average Sale Value Trend" collapsed={!!collapsed['avg_trend']} onToggle={toggleCollapse} headerRight={periodToggle}>
        <RefreshBadge />
        <ReactECharts option={avgOption} style={{ height:'200px' }} />
      </CollapsibleSection>

      {/* ── PERIOD COMPARISON ── */}
      <CollapsibleSection visKey="comparison" title="Period Comparison" collapsed={!!collapsed['comparison']} onToggle={toggleCollapse}
        headerRight={
          <YearSelect
            available={allAvailableYears.length > 0 ? allAvailableYears : availableYears}
            selected={selectedYears}
            onChange={setSY}
          />
        }
      >
        {compLoading ? (
          <div style={{ height:'200px', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <div style={{ width:'24px', height:'24px', border:`2px solid ${C.bgElevated}`, borderTopColor:C.accent, borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
          </div>
        ) : comparisonOption ? (
          <ReactECharts option={comparisonOption} style={{ height:'220px' }} />
        ) : (
          <p style={{ textAlign:'center', color:C.textMuted, fontSize:'12px', padding:'40px 0' }}>Select years above to compare</p>
        )}
      </CollapsibleSection>

      {/* ── DRILL-DOWN REPORT ── */}
      <CollapsibleSection
        visKey="drill"
        title="Drill-Down Report"
        collapsed={!!collapsed['drill']}
        onToggle={toggleCollapse}
        headerRight={
          <div style={toggleGroup}>
            <button style={toggleBtn(drillMetric === 'revenue')} onClick={() => setDM('revenue')}>Revenue</button>
            <button style={toggleBtn(drillMetric === 'qty')}     onClick={() => setDM('qty')}>Qty</button>
          </div>
        }
      >
        {/* Start dimension picker */}
        <div style={{ display:'flex', gap:'5px', flexWrap:'wrap', marginBottom:'8px' }}>
          {DRILL_DIMS.filter(d => d.key !== 'daily').map(d => (
            <button key={d.key}
              onClick={() => resetDrill(d.key)}
              style={{ ...filterPill(drillStack[0].dim === d.key), fontSize:'10px', padding:'3px 8px' }}>
              {d.short}
            </button>
          ))}
        </div>

        <Breadcrumb path={drillPath} onNavigate={drillBack} />

        {currentDim === 'daily' && drillDailyOption ? (
          <ReactECharts option={drillDailyOption} style={{ height:'180px' }} />
        ) : isHorizontal ? (
          <div style={{ overflowY:'auto', maxHeight:'190px', overflowX:'hidden' }}>
            <ReactECharts
              option={drillBarOption}
              style={{ height:`${Math.max(drillData.length * 36 + 16, 160)}px` }}
              onEvents={{ click: (p) => {
                if (!canDrillDeeper) return;
                setPendingItem(drillData[p.dataIndex]);
                setPickerOpen(true);
              }}}
            />
          </div>
        ) : drillBarOption ? (
          <ReactECharts
            option={drillBarOption}
            style={{ height:'220px' }}
            onEvents={{ click: (p) => {
              if (!canDrillDeeper) return;
              setPendingItem(drillData[p.dataIndex]);
              setPickerOpen(true);
            }}}
          />
        ) : null}

        {canDrillDeeper && (
          <p style={{ fontSize:'10px', color:C.textMuted, textAlign:'center', margin:'6px 0 0', fontStyle:'italic' }}>
            Tap a bar to drill deeper
          </p>
        )}

        {currentDim !== 'daily' && (
          <div style={{ marginTop:'10px', display:'flex', flexDirection:'column', gap:'6px' }}>
            {drillData.slice(0, 5).map((item, i) => (
              <div
                key={i}
                onClick={() => {
                  if (!canDrillDeeper) return;
                  setPendingItem(item);
                  setPickerOpen(true);
                }}
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 10px', background:C.bgElevated, borderRadius:'8px', cursor: canDrillDeeper ? 'pointer' : 'default' }}
              >
                <div style={{ display:'flex', alignItems:'center', gap:'8px', minWidth:0 }}>
                  <div style={{ width:'8px', height:'8px', borderRadius:'2px', background:DRILL_COLORS[i % DRILL_COLORS.length], flexShrink:0 }}/>
                  <span style={{ fontSize:'12px', color:C.textSec, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {(item.label || item.key || 'Unknown').length > 28
                      ? (item.label || item.key).slice(0,28)+'…'
                      : (item.label || item.key || 'Unknown')}
                  </span>
                </div>
                <div style={{ textAlign:'right', flexShrink:0, marginLeft:'8px' }}>
                  <p style={{ fontSize:'12px', fontWeight:'600', color:DRILL_COLORS[i % DRILL_COLORS.length], margin:0 }}>
                    {drillMetric === 'qty'
                      ? `${parseInt(item.value||0).toLocaleString()} qty`
                      : `RM ${parseFloat(item.value||0).toLocaleString(undefined,{maximumFractionDigits:0})}`}
                  </p>
                  <p style={{ fontSize:'10px', color:C.textMuted, margin:0 }}>{item.transactions} txns</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* ── DRILL DIM PICKER (fixed overlay) ── */}
      <DrillDimPicker
        open={drillPickerOpen}
        usedDims={usedDims}
        onPick={drillDown}
        onCancel={() => { setPickerOpen(false); setPendingItem(null); }}
      />

      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );
};

export default SalesOverview;